import Vue from 'vue';

import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';

import DashboardConst from '@/constants/dashboard';

const dashboard = {
  state: {
    /**
     * 대시보드 로딩 상태
     * @type {Boolean}
     */
    isLoading: false,
    /**
     * View 모드
     * @type {String}
     */
    viewMode: DashboardConst.VIEW_MODE.MONITORING,
    /**
     * 위젯 템플릿 목록
     * @type {Object}
     */
    widgetTempl: {},
    /**
     * SQL 정보
     * @type {Object}
     */
    sqlInfo: {},
    /**
     * 현재 대시보드 원본 데이터
     * @type {Object}
     */
    orgDashboardData: {},
    /**
     * 현재 대시보드 데이터
     * @type {Object}
     */
    dashboardData: {},
    /**
     * 위젯 View 컴포넌트
     * @type {Object}
     */
    widgetViewComponent: {},
    /**
     * 위젯 Setting 컴포넌트
     * @type {Object}
     */
    widgetSettingComponent: {},

    /**
     * 최대화 위젯 아이디
     * @type {Object}
     */
    expandWidgetId: null,
    /**
     * 그리드 대시보드 트리 데이터
     * @type {Array}
     */
    gridDashboardTree: [],
    /**
     * 좌표 대시보드 트리 데이터
     * @type {Array}
     */
    coordDashboardTree: [],
    isCustomCtrlDashboard: false,
    /**
     * 대시보드 구분 (좌표 / 그리드)
     * @type {[type]}
     */
    dashboardType: DashboardConst.DASHBOARD_TYPE.GRID,
    /**
     * 스케일 맞춤 여부
     * @type {Boolean}
     */
    fullScale: false,
    /**
     * 반응형 옵션화 여부 (true=활성, false=비활성)
     * @type {Boolean}
     */
    isResponsive: true,
    /**
     * 커스텀대시보드 컨트롤바 표현 여부
     * @type {[type]}
     */
    isControlbarVisible: true,
    /**
      * 기본대시보드 테마
      * @type {[type]}
      */
    dashboardTheme: DashboardConst.THEME.WHITE,
    /**
     * 로테이션 정보
     * 로테이션을 그룹핑하는 parent와 parent에 속한 child로 구성된다
     * @type {Object}
     */
    rotationInfo: {
      parentNumber: 1,
      childNumber: 1,
    },
    /**
     * 편집모드 위젯 필터링 로테이션 번호 목록
     * @type {Array}
     */
    editFilterRotationIds: [],
    /**
     * 전체화면 여부
     * @type {Boolean}
     */
    fullScreen: false,
    /**
     * 슬라이드쇼 실행 여부
     * @type {Boolean}
     */
    slideshow: false,
    /**
     * 화면 좌측 영역 모드 (위젯모음 or 위젯설정)
     * @type {[type]}
     */
    sideSettingType: DashboardConst.SIDE_SETTING_TYPE.ELEMENT, // element or setting
    /**
     * 편집 > 위젯 선택 모드
     * @type {[type]}
     */
    editSelectMode: DashboardConst.EDIT_SELECT_MODE.SELECTION,
  },
  getters: {
    getDashboardViewMode(state) {
      return state.viewMode;
    },
    getDashboardData(state) {
      return state.dashboardData;
    },
    getWidgetTempl(state) {
      return state.widgetTempl;
    },
    getSqlInfo(state) {
      return state.sqlInfo;
    },
    getWidgetViewComponent(state) {
      return state.widgetViewComponent;
    },
    getWidgetSettingComponent(state) {
      return state.widgetSettingComponent;
    },
    getExpandWidgetId(state) {
      return state.expandWidgetId;
    },
    getDashboardTree(state) {
      return (dashboardType) => {
        if (DashboardConst.DASHBOARD_TYPE.COORD === dashboardType) {
          return state.coordDashboardTree;
        }
        return state.gridDashboardTree;
      };
    },
    getIsCustomCtrlDashboard(state) {
      return state.isCustomCtrlDashboard;
    },
    getDashboardType(state) {
      return state.dashboardType;
    },
    getDashboardFullScale(state) {
      return state.fullScale;
    },
    getResponsiveLayout(state) {
      return state.isResponsive;
    },
    getCoordDashboardControlbar(state) {
      return state.isControlbarVisible;
    },
    getDashboardTheme(state) {
      return state.dashboardTheme;
    },
    getRotationInfo(state) {
      return state.rotationInfo;
    },
    getEditFilterRotationIds(state) {
      return state.editFilterRotationIds;
    },
    getDashboardFullScreen(state) {
      return state.fullScreen;
    },
    getDashboardSlideshow(state) {
      return state.slideshow;
    },
    getSideSettingType(state) {
      return state.sideSettingType;
    },
    getEditSelectMode(state) {
      return state.editSelectMode;
    },
  },
  mutations: {
    setDashboardViewMode(state, viewMode) {
      state.viewMode = viewMode;
    },
    setDashboardData(state, dashboardData) {
      state.dashboardData = dashboardData;
      state.orgDashboardData = cloneDeep(dashboardData);
    },
    setWidgetTempl(state, widgetTempl) {
      state.widgetTempl = widgetTempl;
    },
    setSqlInfo(state, sqlInfo) {
      state.sqlInfo = sqlInfo;
    },
    setWidgetViewComponent(state, widgetViewComponent) {
      state.widgetViewComponent = widgetViewComponent;
    },
    setWidgetSettingComponent(state, widgetSettingComponent) {
      state.widgetSettingComponent = widgetSettingComponent;
    },
    setExpandWidgetId(state, widgetId) {
      state.expandWidgetId = widgetId;
    },
    setDashboardTree(state, { dashboardTree, dashboardType }) {
      if (DashboardConst.DASHBOARD_TYPE.COORD === dashboardType) {
        state.coordDashboardTree = dashboardTree;
      } else {
        state.gridDashboardTree = dashboardTree;
      }
    },
    resetDashboardData(state, dashboardData) {
      state.dashboardData = {};
      Vue.nextTick(() => {
        if (dashboardData) {
          state.dashboardData = cloneDeep(dashboardData);
        } else {
          state.dashboardData = cloneDeep(state.orgDashboardData);
        }
      });
    },
    addWidget(state, widget) {
      Vue.set(state.dashboardData.dashboardWidget, widget.i, {
        dashboardId: state.dashboardData.dashboardId,
        widgetId: widget.i,
        widgetTemplId: widget.widgetTemplId,
        widgetTitle: state.widgetTempl[widget.widgetTemplId].widgetTemplName,
        widgetDesc: state.widgetTempl[widget.widgetTemplId].widgetTemplDesc,
        widgetInfo: {
          baseOptions: {},
          dataOptions: {},
          viewOptions: {},
        },
        titleFlag: 'Y',
      });

      state.dashboardData.layoutInfo.push(widget);
    },
    removeWidget(state, widgetId) {
      Vue.delete(state.dashboardData.dashboardWidget, widgetId);

      const layoutIndex = findIndex(state.dashboardData.layoutInfo, ['i', widgetId]);
      if (layoutIndex > -1) {
        state.dashboardData.layoutInfo.splice(layoutIndex, 1);
      }
    },
    updateWidget(state, widget) {
      state.dashboardData.dashboardWidget[widget.widgetId] = widget;
    },
    addWidgetLayout(state, widget) {
      state.dashboardData.layoutInfo.push(widget);
    },
    setIsCustomCtrlDashboard(state, isCustomCtrlDashboard) {
      state.isCustomCtrlDashboard = isCustomCtrlDashboard;
    },
    setDashboardType(state, dashboardType) {
      state.dashboardType = dashboardType;
    },
    setDashboardFullScale(state, fullScale) {
      state.fullScale = fullScale;

      // 로컬스토리지에 저장
      localStorage.setItem('dashboardFullScale', fullScale);
    },
    setResponsiveLayout(state, isResponsive) {
      state.isResponsive = isResponsive;

      // 로컬스토리지에 저장
      localStorage.setItem('responsiveLayout', isResponsive);
    },
    setCoordDashboardControlbar(state, isControlbarVisible) {
      state.isControlbarVisible = isControlbarVisible;

      // 로컬스토리지에 저장
      localStorage.setItem('coordDashboardControlbar', isControlbarVisible);
    },
    setDashboardTheme(state, dashboardTheme) {
      state.dashboardTheme = dashboardTheme;
      localStorage.setItem('dashboardTheme', dashboardTheme);
    },
    setRotationInfo(state, rotationInfo) {
      state.rotationInfo = rotationInfo;
    },
    setEditFilterRotationIds(state, rotationIds) {
      state.editFilterRotationIds = rotationIds;
    },
    setDashboardFullScreen(state, fullScreen) {
      state.fullScreen = fullScreen;

      // 로컬스토리지에 저장
      if (fullScreen) {
        localStorage.setItem('dashboardFullScreen', state.dashboardType);
      } else {
        localStorage.removeItem('dashboardFullScreen');
      }
    },
    setDashboardSlideshow(state, slideshow) {
      state.slideshow = slideshow;
    },
    setSideSettingType(state, sideSettingType) {
      state.sideSettingType = sideSettingType;
    },
    setEditSelectMode(state, editSelectMode) {
      state.editSelectMode = editSelectMode;
    },
  },
  actions: {
    clearDashboard({ commit }) {
      commit('setDashboardViewMode', DashboardConst.VIEW_MODE.MONITORING);
      commit('setWidgetTempl', {});
      commit('setSqlInfo', {});
      commit('setDashboardData', {});
      commit('setExpandWidgetId', null);
      commit('setIsCustomCtrlDashboard', false);
      commit('setDashboardFullScale', localStorage.getItem('dashboardFullScale') === 'true');
      commit('setResponsiveLayout', localStorage.getItem('responsiveLayout') === 'true');
      commit('setCoordDashboardControlbar', localStorage.getItem('coordDashboardControlbar') === 'true');
      commit('setDashboardTheme', Object.prototype.hasOwnProperty.call(localStorage, 'dashboardTheme') ? localStorage.getItem('dashboardTheme') : DashboardConst.THEME.WHITE);
      commit('setRotationInfo', {
        parentNumber: 1,
        childNumber: 1,
      });
      commit('setEditFilterRotationIds', [1]);
      commit('setSideSettingType', DashboardConst.SIDE_SETTING_TYPE.ELEMENT);
      commit('setEditSelectMode', DashboardConst.EDIT_SELECT_MODE.SELECTION);
    },
  },
};

export default dashboard;



// WEBPACK FOOTER //
// ./src/store/modules/dashboard.js